/* Left column — the SOURCES panel (DESIGN5 §FG2), shown as a three-step
   wizard rather than one long scroll (owner task #1 «левое меню сделать
   поэтапно»):
     1. Sources  — the main call transcript (paste or upload .txt, char
        counter, debounced autosave to PUT /inputs {main_transcript}) and
        the additional files (dropzone + list, each row with its own AI
        note — debounced PATCH /files/:id {note}).
     2. Cases    — the optional case plan (reorderable rows, autosaved to
        PUT /inputs {phases}).
     3. Generate — the extra instructions (autosaved to {extra_notes}) and
        the Generate button, disabled while a run is active.
   Steps slide sideways (framer-motion, direction-aware). Every value lives
   in this component's state, so a step that is off screen keeps its text
   and its pending autosaves — only the visible markup changes. Autosave is
   debounced (800ms) and flushes on blur; last-write-wins is fine per the
   contract. Client guards (≤10 files, ≤25 MB) live in
   EditorView.uploadFiles. */

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  Transition,
  Variants,
} from 'framer-motion';
import { FileMeta } from './types';
import { architectApi, Phase, ProjectInputs } from './api';
import { toast } from './toast';
import type { WorkspaceDraftState } from './useWorkspaceDraftGuard';
import { fmtSize, autoGrow, swap } from './utils';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderInput,
  Layers3,
  MessageSquareText,
  Plus,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import './sources-ui.css';

/** Imperative surface for EditorView: before a generate/prompt run is
    POSTed, every pending debounced autosave must be flushed AND awaited,
    or the run would read stale inputs from the DB. */
export interface FilesPanelHandle {
  flushAllPending: () => Promise<void>;
  getDraftState: () => WorkspaceDraftState;
}

type SaveState = 'idle' | 'saving' | 'saved';

interface PhaseRow {
  title: string;
  description: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  ready: boolean; // the project (with its inputs) has loaded
  files: FileMeta[];
  initialTranscript: string;
  initialExtra: string;
  initialPhases: Phase[];
  onUpload: (files: FileList | File[]) => void;
  onDeleteFile: (f: FileMeta) => void;
  onFileNoteSaved: (fileId: string, note: string) => void;
  onGenerate: () => void;
  running: boolean;
  generating: boolean; // running && this is a generate run
  // Desktop collapse (DESIGN9 §3): the left column slides to a 34px rail.
  collapsed: boolean;
  onSetCollapsed: (v: boolean) => void;
  /* DESIGN39 — hover-peek: while collapsed, pointing at the rail slides the
     panel out OVER the canvas without reflowing the layout, and clicking the
     rail gives this side the room (the opposite panel folds away). */
  peeking: boolean;
  onPeekEnter: () => void;
  onPeekLeave: () => void;
  onRailClick: () => void;
}

const MAX_TRANSCRIPT = 300_000;
const MAX_EXTRA = 20_000;
const MAX_PHASES = 12;
const MAX_PHASE_TITLE = 120;
const MAX_PHASE_DESC = 2000;
const MAX_NOTE = 1000;

/* ── wizard steps ─────────────────────────────────────── */
type StepId = 0 | 1 | 2;
const STEPS: { label: string; hint: string }[] = [
  { label: 'Sources', hint: 'Call transcript and additional files' },
  { label: 'Cases', hint: 'Optional — the cases the PDF should cover' },
  { label: 'Generate', hint: 'Extra instructions, then generate' },
];
const LAST_STEP = (STEPS.length - 1) as StepId;
// How far a pane travels while it slides in/out.
const SLIDE_PX = 44;

/* The step a project was last on survives a reload within the tab, so a
   returning operator lands where they left off. */
const stepKey = (projectId: string) => 'arch-sources-step:' + projectId;
function readStep(projectId: string): StepId | null {
  try {
    const raw = window.sessionStorage.getItem(stepKey(projectId));
    if (raw === '0' || raw === '1' || raw === '2') return Number(raw) as StepId;
  } catch {
    /* storage blocked (private mode) — start from the default */
  }
  return null;
}
function writeStep(projectId: string, step: StepId): void {
  try {
    window.sessionStorage.setItem(stepKey(projectId), String(step));
  } catch {
    /* ignore — the step just won't be remembered */
  }
}

function formatChars(n: number): string {
  // "12 340 chars" — thin space thousands separator.
  return n.toLocaleString('en-US').replace(/,/g, ' ') + ' chars';
}

function SaveHint({ state }: { state: SaveState }) {
  if (state === 'saving') return <span className="arch-save-hint">Saving…</span>;
  if (state === 'saved')
    return <span className="arch-save-hint ok"><Check size={12} aria-hidden="true" /> Saved</span>;
  return null;
}

const FilesPanel = React.forwardRef<FilesPanelHandle, Props>(function FilesPanel({
  open,
  onClose,
  projectId,
  ready,
  files,
  initialTranscript,
  initialExtra,
  initialPhases,
  onUpload,
  onDeleteFile,
  onFileNoteSaved,
  onGenerate,
  running,
  generating,
  collapsed,
  onSetCollapsed,
  peeking,
  onPeekEnter,
  onPeekLeave,
  onRailClick,
}: Props, ref) {
  const asideRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txtInputRef = useRef<HTMLInputElement>(null);
  const transcriptRef = useRef<HTMLTextAreaElement>(null);
  const extraRef = useRef<HTMLTextAreaElement>(null);
  const [dragover, setDragover] = useState(false);

  const [transcript, setTranscript] = useState('');
  const [extra, setExtra] = useState('');
  const [phases, setPhases] = useState<PhaseRow[]>([]);
  // Per-file AI notes are kept here (not as uncontrolled inputs) so the
  // Sources step can unmount and come back without losing a note that is
  // still inside its debounce window.
  const [notes, setNotes] = useState<Record<string, string>>({});

  const [tStatus, setTStatus] = useState<SaveState>('idle');
  const [eStatus, setEStatus] = useState<SaveState>('idle');
  const [pStatus, setPStatus] = useState<SaveState>('idle');
  const [noteStatus, setNoteStatus] = useState<Record<string, SaveState>>({});

  // Seed the local editors once, when the project's inputs first arrive.
  const seeded = useRef(false);
  useEffect(() => {
    if (!ready || seeded.current) return;
    seeded.current = true;
    setTranscript(initialTranscript || '');
    setExtra(initialExtra || '');
    setPhases(
      (initialPhases || []).map((p) => ({
        title: p.title || '',
        description: p.description || '',
      })),
    );
  }, [ready, initialTranscript, initialExtra, initialPhases]);

  /* ── wizard state ────────────────────────────────────── */
  const [chosenStep, setChosenStep] = useState<StepId | null>(null);
  const [dir, setDir] = useState<1 | -1>(1);
  // Derived, not seeded in an effect: the first ready render already shows
  // the right step, so nothing slides on load. A project that already has
  // its transcript opens on the Generate step — the next thing to do.
  const defaultStep = useMemo<StepId>(() => {
    if (!ready) return 0;
    const hasText = !!(initialTranscript || '').trim() || !!(initialExtra || '').trim() || files.some(f => f.text_len > 0);
    const remembered = readStep(projectId);
    if (remembered != null && (remembered === 0 || hasText)) return remembered;
    return hasText ? LAST_STEP : 0;
  }, [ready, projectId, initialTranscript, initialExtra, files]);
  const step: StepId = chosenStep ?? defaultStep;
  const goTo = (next: StepId) => {
    if (next === step) return;
    setDir(next > step ? 1 : -1);
    setChosenStep(next);
    writeStep(projectId, next);
  };
  useEffect(() => {
    // A new step starts at the top of the panel — whichever element scrolls
    // (the aside normally, the content while the panel is collapsed+peeked).
    if (asideRef.current) asideRef.current.scrollTop = 0;
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [step]);

  const reduced = useReducedMotion();
  const slide = reduced ? 0 : SLIDE_PX;
  const variants: Variants = {
    enter: (d: number) => ({ x: d * slide, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: -d * slide, opacity: 0 }),
  };
  // Same curve as the panel's own collapse (cubic-bezier(.4,0,.2,1) 0.3s).
  const transition: Transition = reduced
    ? { duration: 0.12 }
    : {
        x: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
        opacity: { duration: 0.2, ease: 'easeOut' },
      };

  const hasSource = transcript.trim().length > 0 || extra.trim().length > 0 || files.some(f => f.text_len > 0);
  const hasCases = phases.some((p) => p.description.trim());
  // Match the generation endpoint: transcript, readable files or saved notes supply the source.
  const done = [hasSource, hasCases, false];

  // Debounce timers, keyed so each field/file has its own. The pending
  // fn is kept alongside its timer so flushAllPending can run every not-
  // yet-fired save immediately; in-flight save promises are tracked so
  // the flush can be AWAITED (a run must never read stale inputs).
  const timers = useRef<Record<string, number>>({});
  const pendingFns = useRef<Record<string, () => unknown>>({});
  const inFlight = useRef<Set<Promise<unknown>>>(new Set());
  // Navigation must see edits inside the debounce window and after a failed
  // autosave. Save hints alone cannot distinguish those from untouched inputs.
  const saveRevisions = useRef<Record<string, number>>({});
  const unsavedKeys = useRef(new Set<string>());
  const markPending = (key: string) => {
    saveRevisions.current[key] = (saveRevisions.current[key] || 0) + 1;
    unsavedKeys.current.add(key);
  };
  const finishSaved = (key: string, revision: number): boolean => {
    if (saveRevisions.current[key] === revision) {
      unsavedKeys.current.delete(key);
      return true;
    }
    // A late response must not clear a newer edit. If it arrives after a
    // newer response, the server's final value is not safe to assume either.
    unsavedKeys.current.add(key);
    return false;
  };
  const getDraftState = useCallback(() => ({
    dirty: unsavedKeys.current.size > 0,
    busy: inFlight.current.size > 0,
  }), []);
  const track = (p: unknown): void => {
    const pr = Promise.resolve(p).catch(() => {});
    inFlight.current.add(pr);
    pr.then(() => {
      inFlight.current.delete(pr);
    });
  };
  const schedule = (key: string, fn: () => unknown, delay = 800) => {
    markPending(key);
    if (timers.current[key]) window.clearTimeout(timers.current[key]);
    pendingFns.current[key] = fn;
    timers.current[key] = window.setTimeout(() => {
      delete timers.current[key];
      delete pendingFns.current[key];
      track(fn());
    }, delay);
  };
  const flush = (key: string, fn: () => unknown) => {
    if (timers.current[key]) {
      window.clearTimeout(timers.current[key]);
      delete timers.current[key];
      delete pendingFns.current[key];
      track(fn());
    }
  };
  /** Fire every pending debounced save NOW and resolve once every
      in-flight save (incl. ones already running) has settled. */
  const flushAllPending = useCallback(async () => {
    const fns = pendingFns.current;
    pendingFns.current = {};
    Object.values(timers.current).forEach((id) => window.clearTimeout(id));
    timers.current = {};
    Object.values(fns).forEach((fn) => track(fn()));
    // Loop: a save finishing must not leave a later-added one unawaited.
    while (inFlight.current.size) {
      await Promise.all(Array.from(inFlight.current));
    }
  }, []);
  useImperativeHandle(ref, () => ({ flushAllPending, getDraftState }), [flushAllPending, getDraftState]);
  useEffect(
    () => () => {
      Object.values(timers.current).forEach((id) => window.clearTimeout(id));
      timers.current = {};
      pendingFns.current = {};
    },
    [],
  );

  const doSaveInputs = async (
    patch: ProjectInputs,
    setStatus: (s: SaveState) => void,
  ) => {
    const key = 'main_transcript' in patch ? 'transcript' : 'extra_notes' in patch ? 'extra' : 'phases';
    const revision = saveRevisions.current[key];
    setStatus('saving');
    try {
      await architectApi.putInputs(projectId, patch);
      setStatus(finishSaved(key, revision) ? 'saved' : 'idle');
    } catch (err: any) {
      setStatus('idle');
      toast(err.message, 'err');
    }
  };
  // Every save goes through track() so flushAllPending can await saves
  // that are ALREADY in flight, not just the debounced pending ones.
  const saveInputs = (
    patch: ProjectInputs,
    setStatus: (s: SaveState) => void,
  ): Promise<void> => {
    const p = doSaveInputs(patch, setStatus);
    track(p);
    return p;
  };

  /* ── the two long-text fields ────────────────────────── */
  // The transcript and the extra instructions hold long text, and each now
  // has a step of its own, so they auto-grow to their content up to a share
  // of the viewport (then they scroll); the CSS min-height is the floor
  // that keeps them a generous size even while empty. Runs on every value
  // change, including the seed and .txt uploads, and again when the step
  // comes back on screen (the textarea is remounted then).
  const fitTextarea = (ta: HTMLTextAreaElement | null, maxVh: number) => {
    if (!ta) return;
    ta.style.height = 'auto';
    const max = Math.round(window.innerHeight * maxVh);
    ta.style.height = Math.min(ta.scrollHeight + 2, max) + 'px';
    ta.style.overflowY = ta.scrollHeight + 2 > max ? 'auto' : 'hidden';
  };
  useLayoutEffect(() => fitTextarea(transcriptRef.current, 0.46), [transcript, step]);
  useLayoutEffect(() => fitTextarea(extraRef.current, 0.5), [extra, step]);

  /* ── main transcript ─────────────────────────────────── */

  const saveTranscript = () =>
    saveInputs({ main_transcript: transcript }, setTStatus);

  const onTranscriptChange = (v: string) => {
    setTranscript(v);
    schedule('transcript', () =>
      saveInputs({ main_transcript: v }, setTStatus),
    );
  };

  const onUploadTxt = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      if (!text.trim()) {
        toast('That file is empty', 'err');
        return;
      }
      if (
        transcript.trim() &&
        !window.confirm('Replace the current transcript with the uploaded file?')
      )
        return;
      const capped = text.slice(0, MAX_TRANSCRIPT);
      setTranscript(capped);
      flush('transcript', () => undefined);
      markPending('transcript');
      saveInputs({ main_transcript: capped }, setTStatus);
    };
    reader.onerror = () => toast('Could not read that file', 'err');
    reader.readAsText(file);
  };

  /* ── extra instruction ───────────────────────────────── */
  const onExtraChange = (v: string) => {
    setExtra(v);
    schedule('extra', () => saveInputs({ extra_notes: v }, setEStatus));
  };

  /* ── phases ──────────────────────────────────────────── */
  const savePhases = (rows: PhaseRow[]): Promise<void> => {
    const payload: Phase[] = rows
      .filter((r) => r.description.trim())
      .map((r) => {
        const p: Phase = { description: r.description.trim() };
        if (r.title.trim()) p.title = r.title.trim();
        return p;
      });
    return saveInputs({ phases: payload }, setPStatus);
  };

  const commitPhases = (rows: PhaseRow[]) => {
    setPhases(rows);
    schedule('phases', () => savePhases(rows));
  };

  const setPhaseField = (i: number, key: keyof PhaseRow, v: string) => {
    const rows = phases.slice();
    rows[i] = { ...rows[i], [key]: v };
    commitPhases(rows);
  };
  const addPhase = () => {
    if (phases.length >= MAX_PHASES) return;
    commitPhases(phases.concat({ title: '', description: '' }));
  };
  const removePhase = (i: number) => {
    const rows = phases.slice();
    rows.splice(i, 1);
    commitPhases(rows);
  };
  const movePhase = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= phases.length) return;
    const rows = phases.slice();
    swap(rows, i, j);
    commitPhases(rows);
  };

  /* ── per-file AI note ────────────────────────────────── */
  const onNoteChange = (fileId: string, note: string) => {
    setNotes((s) => ({ ...s, [fileId]: note }));
    const key = 'note:' + fileId;
    schedule(key, async () => {
      const revision = saveRevisions.current[key];
      setNoteStatus((s) => ({ ...s, [fileId]: 'saving' }));
      try {
        await architectApi.patchFileNote(projectId, fileId, note);
        const saved = finishSaved(key, revision);
        setNoteStatus((s) => ({ ...s, [fileId]: saved ? 'saved' : 'idle' }));
        onFileNoteSaved(fileId, note);
      } catch (err: any) {
        setNoteStatus((s) => ({ ...s, [fileId]: 'idle' }));
        toast(err.message, 'err');
      }
    });
  };

  /* ── step 1: sources (transcript + files) ────────────── */
  const sourcesStep = (
    <>
      <div className="arch-panel-section">
        <div className="arch-section-head">
          <h3><MessageSquareText size={15} aria-hidden="true" /> Call transcript</h3>
          <span className="arch-source-tag">Main source</span>
          <SaveHint state={tStatus} />
        </div>
        <textarea
          ref={transcriptRef}
          aria-label="Call transcript"
          className="arch-full-width arch-transcript"
          placeholder="Paste the call transcript here…"
          maxLength={MAX_TRANSCRIPT}
          value={transcript}
          onChange={(e) => onTranscriptChange(e.target.value)}
          onBlur={() => flush('transcript', saveTranscript)}
        />
        <div className="arch-transcript-foot">
          <button
            type="button"
            className="arch-btn arch-btn-outline arch-btn-sm"
            onClick={() => txtInputRef.current?.click()}
          >
            <Upload size={14} aria-hidden="true" /> Upload .txt
          </button>
          <span className="arch-char-count">{formatChars(transcript.length)}</span>
          <input
            ref={txtInputRef}
            type="file"
            accept=".txt,.md,.text,text/plain"
            hidden
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) onUploadTxt(f);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div className="arch-panel-section">
        <div className="arch-section-head">
          <h3><FolderInput size={15} aria-hidden="true" /> Additional files</h3>
          <span className="arch-source-tag">{files.length} / 10</span>
        </div>
        <div
          className={'arch-dropzone' + (dragover ? ' dragover' : '')}
          onDragEnter={(e) => {
            e.preventDefault();
            setDragover(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragover(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setDragover(false);
          }}
          onDrop={(e) => {
            e.preventDefault();
            setDragover(false);
            if (e.dataTransfer && e.dataTransfer.files.length) {
              onUpload(e.dataTransfer.files);
            }
          }}
        >
          <span className="arch-source-upload-icon"><Upload size={19} aria-hidden="true" /></span>
          <div className="arch-source-upload-main">
            <div className="arch-dropzone-hint">Drag files here, or</div>
            <button
              type="button"
              className="arch-btn arch-btn-outline arch-btn-sm"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose files
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) onUpload(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
          <div className="arch-dropzone-note">
            Up to 25 MB each · TXT, MD, PDF, DOCX, CSV…
          </div>
        </div>
        <ul className="arch-file-list">
          {files.map((f) => (
            <li className="arch-file-card" key={f.id}>
              <div className="arch-file-row">
                <span className="arch-source-file-icon"><FileText size={17} aria-hidden="true" /></span>
                <div className="arch-source-file-info">
                  <span className="arch-f-name" title={f.filename}>
                    {f.filename}
                  </span>
                  <span className="arch-f-size">{fmtSize(f.size_bytes)}</span>
                </div>
                <button
                  className="arch-icon-btn"
                  title="Delete file"
                  aria-label={`Delete file ${f.filename}`}
                  onClick={() => onDeleteFile(f)}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              </div>
              <div className="arch-file-note">
                <input
                  type="text"
                  placeholder="Add a note for the AI…"
                  aria-label={`AI note for ${f.filename}`}
                  maxLength={MAX_NOTE}
                  value={notes[f.id] ?? f.note ?? ''}
                  onChange={(e) => onNoteChange(f.id, e.target.value)}
                />
                <SaveHint state={noteStatus[f.id] || 'idle'} />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  /* ── step 2: requested cases ──
     UI SAYS «CASES» (owner feedback #2): one project = one PHASE = one
     PDF document, and the sections the operator plans here are the CASES
     inside it. Only the visible strings changed — the wire field stays
     `phases` (PUT /inputs {phases}) and the internal names follow it, so
     no API/migration churn. */
  const casesStep = (
    <div className="arch-panel-section">
      <div className="arch-section-head">
        <h3><Layers3 size={15} aria-hidden="true" /> Requested cases</h3>
        <span className="arch-source-tag">Optional</span>
        <SaveHint state={pStatus} />
      </div>
      <p className="arch-section-hint">
        Outline what each case covers. All cases appear in one architecture PDF.
      </p>
      <div className="arch-phase-list">
        {phases.map((p, i) => (
          <div className="arch-phase-row" key={i}>
            <div className="arch-phase-head">
              <span className="arch-phase-chip">Case {i + 1}</span>
              <div className="arch-phase-ctrls">
                <button
                  className="arch-icon-btn move"
                  title="Move up"
                  aria-label="Move case up"
                  disabled={i === 0}
                  onClick={() => movePhase(i, -1)}
                >
                  <ArrowUp size={14} aria-hidden="true" />
                </button>
                <button
                  className="arch-icon-btn move"
                  title="Move down"
                  aria-label="Move case down"
                  disabled={i === phases.length - 1}
                  onClick={() => movePhase(i, 1)}
                >
                  <ArrowDown size={14} aria-hidden="true" />
                </button>
                <button
                  className="arch-icon-btn"
                  title="Remove case"
                  aria-label="Remove case"
                  onClick={() => removePhase(i)}
                >
                  <X size={15} aria-hidden="true" />
                </button>
              </div>
            </div>
            <input
              type="text"
              className="arch-full-width"
              placeholder="Title (optional)"
              aria-label={`Case ${i + 1} title`}
              maxLength={MAX_PHASE_TITLE}
              value={p.title}
              onChange={(e) => setPhaseField(i, 'title', e.target.value)}
            />
            <textarea
              className="arch-full-width arch-phase-desc"
              placeholder="What this case covers…"
              aria-label={`Case ${i + 1} description`}
              maxLength={MAX_PHASE_DESC}
              value={p.description}
              ref={(el) => {
                if (el) autoGrow(el);
              }}
              onChange={(e) => {
                autoGrow(e.target as HTMLTextAreaElement);
                setPhaseField(i, 'description', e.target.value);
              }}
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        className="arch-list-add"
        disabled={phases.length >= MAX_PHASES}
        onClick={addPhase}
      >
        <Plus size={15} aria-hidden="true" /> Add case
      </button>
    </div>
  );

  /* ── step 3: extra instructions (Generate lives in the footer) ── */
  const generateStep = (
    <div className="arch-panel-section">
      <div className="arch-section-head">
        <h3><MessageSquareText size={15} aria-hidden="true" /> Extra instructions</h3>
        <span className="arch-source-tag">Optional</span>
        <SaveHint state={eStatus} />
      </div>
      <textarea
        ref={extraRef}
        className="arch-full-width arch-extra"
        placeholder="Global guidance for the AI (optional)…"
        aria-label="Extra instructions"
        maxLength={MAX_EXTRA}
        value={extra}
        onChange={(e) => onExtraChange(e.target.value)}
        onBlur={() =>
          flush('extra', () => saveInputs({ extra_notes: extra }, setEStatus))
        }
      />
    </div>
  );

  const panes = [sourcesStep, casesStep, generateStep];

  return (
    <aside
      ref={asideRef}
      className={
        'arch-left-panel' +
        (open ? ' open' : '') +
        (collapsed ? ' collapsed' : '') +
        (collapsed && peeking ? ' peek' : '')
      }
      onMouseEnter={collapsed ? onPeekEnter : undefined}
      onMouseLeave={collapsed ? onPeekLeave : undefined}
      onFocus={collapsed ? onPeekEnter : undefined}
      onBlur={collapsed ? onPeekLeave : undefined}
    >
      {/* collapsed rail (desktop only) — hovering peeks, clicking gives this
          side the room; the head chevron pins a peek open instead. */}
      <button
        type="button"
        className="arch-sources-rail"
        title="Show sources (folds the editor away)"
        aria-label="Show sources"
        onClick={onRailClick}
      >
        <span className="arch-sources-rail-chev" aria-hidden="true">
          <ChevronRight size={15} aria-hidden="true" />
        </span>
        <span className="arch-sources-rail-label">SOURCES</span>
        {running ? (
          <span className="arch-sources-rail-dot" aria-hidden="true" />
        ) : null}
      </button>

      <div className="arch-sources-content" ref={contentRef}>
        {/* header row — carries the collapse chevron (desktop only) */}
        <div className="arch-sources-head">
          <span className="arch-sources-head-title"><FolderInput size={17} aria-hidden="true" /> Sources</span>
          <button type="button" className="arch-btn arch-btn-ghost arch-btn-sm arch-mobile-only" aria-label="Close sources panel" onClick={onClose}>
            <X size={16} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="arch-sources-collapse"
            title={collapsed ? 'Keep sources open' : 'Hide sources'}
            aria-label={collapsed ? 'Keep sources open' : 'Hide sources'}
            onClick={() => onSetCollapsed(!collapsed)}
          >
            {collapsed ? <ChevronRight size={16} aria-hidden="true" /> : <ChevronLeft size={16} aria-hidden="true" />}
          </button>
        </div>

        {/* stepper — every step is a button, so a returning operator can
            jump straight to Generate; steps 2–3 unlock with the transcript */}
        <nav className="arch-wizard-steps" aria-label="Sources steps">
          {STEPS.map((s, i) => {
            const active = i === step;
            const locked = i > 0 && !hasSource;
            return (
              <React.Fragment key={s.label}>
                {i > 0 ? (
                  <span
                    className={'arch-wizard-conn' + (done[i - 1] ? ' done' : '')}
                    aria-hidden="true"
                  />
                ) : null}
                <button
                  type="button"
                  className={
                    'arch-wizard-step' +
                    (active ? ' active' : done[i] ? ' done' : '')
                  }
                  aria-label={`Step ${i + 1}: ${s.label}`}
                  aria-current={active ? 'step' : undefined}
                  title={locked ? 'Add the call transcript first' : s.hint}
                  disabled={locked}
                  onClick={() => goTo(i as StepId)}
                >
                  <span className="arch-wizard-dot" aria-hidden="true">
                    {done[i] && !active ? <Check size={11} /> : i + 1}
                  </span>
                  <span className="arch-wizard-label">{s.label}</span>
                </button>
              </React.Fragment>
            );
          })}
        </nav>

        {/* the active step slides in from the side it is coming from; the
            outgoing one leaves the other way (popLayout keeps the height
            from jumping while both are on screen) */}
        {ready ? (
          <div className="arch-wizard-body">
            <AnimatePresence initial={false} custom={dir} mode="popLayout">
              <motion.div
                key={step}
                className="arch-wizard-pane"
                custom={dir}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
              >
                {panes[step]}
              </motion.div>
            </AnimatePresence>
          </div>
        ) : null}

        {/* footer — sticks to the bottom of the panel: Back / Next, and on
            the last step the Generate button itself */}
        <div className="arch-wizard-foot">
          {step > 0 ? (
            <button
              type="button"
              className="arch-btn arch-btn-ghost arch-btn-sm arch-wizard-back"
              onClick={() => goTo((step - 1) as StepId)}
            >
              <ChevronLeft size={15} aria-hidden="true" /> Back
            </button>
          ) : !hasSource ? (
            <span className="arch-wizard-foot-hint">
              Add a transcript or a readable source file to continue
            </span>
          ) : null}
          {step < LAST_STEP ? (
            <button
              type="button"
              className="arch-btn arch-btn-primary arch-wizard-next"
              disabled={!hasSource}
              onClick={() => goTo((step + 1) as StepId)}
            >
              Next <ChevronRight size={15} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              className="arch-btn arch-btn-primary arch-wizard-generate"
              disabled={running}
              onClick={onGenerate}
            >
              {generating ? <span className="arch-spinner" /> : <Sparkles size={16} aria-hidden="true" />}
              <span>{generating ? 'Generating…' : 'Generate architecture'}</span>
            </button>
          )}
        </div>
      </div>
    </aside>
  );
});

export default FilesPanel;
